
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Session, User } from '@supabase/supabase-js';
import { UserProfile } from '@/types/supabase';
import { RoleViewMode } from './types';
import { fetchUserProfile, fetchUserRoles } from './authOperations';

// Define the context type
interface AuthContextProps {
  session: Session | null;
  user: User | null;
  profile: UserProfile | null;
  isLoading: boolean;
  userRoles: string[];
  viewMode: RoleViewMode;
  setViewMode: (mode: RoleViewMode) => void;
  signIn: (email: string) => Promise<void>;
  signInWithPassword: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, metadata?: { first_name?: string; last_name?: string }) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  isAdmin: () => boolean;
  isMentor: () => boolean;
  isStudent: () => boolean;
  canAccessAdminFeatures: () => boolean;
  canAccessMentorFeatures: () => boolean;
  canAccessStudentFeatures: () => boolean;
  canAccessAgenticFeatures: () => boolean;
  resetViewMode: () => void;
}

export const AuthContext = createContext<AuthContextProps | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<RoleViewMode>('default');

  // Fetch session on mount
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Fetch user profile and roles when user changes
  useEffect(() => {
    if (user) {
      refreshProfile();
    } else {
      setProfile(null);
      setUserRoles([]);
    }
  }, [user]);

  // Fetch user profile
  const refreshProfile = async () => {
    if (!user) return;
    
    try {
      const profileData = await fetchUserProfile(user.id);
      if (profileData) {
        // Ensure second_me_config is properly typed as Record<string, any> | null
        const typedProfile: UserProfile = {
          ...profileData,
          second_me_config: profileData.second_me_config 
            ? (profileData.second_me_config as unknown as Record<string, any>) 
            : null
        };
        setProfile(typedProfile);
      }
      
      const roles = await fetchUserRoles(user.id);
      setUserRoles(roles);
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  // Sign in function (magic link)
  const signIn = async (email: string) => {
    const { error } = await supabase.auth.signInWithOtp({ email });
    if (error) throw error;
  };

  // Sign in with password
  const signInWithPassword = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  // Sign up function
  const signUp = async (
    email: string,
    password: string,
    metadata?: { first_name?: string; last_name?: string }
  ) => {
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    });
    if (error) throw error;
  };

  // Sign out function
  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  // Update profile function
  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('No user logged in');

    const { error } = await supabase
      .from('user_profiles')
      .update(updates)
      .eq('id', user.id);

    if (error) throw error;

    refreshProfile();
  };

  // Check if user has a specific role
  const hasRole = (role: string) => {
    return userRoles.includes(role);
  };

  // Check if user is admin
  const isAdmin = () => {
    return hasRole('admin');
  };

  // Check if user is mentor
  const isMentor = () => {
    return hasRole('mentor');
  };

  // Check if user is student
  const isStudent = () => {
    return hasRole('student') || userRoles.length === 0; // All users are students by default
  };

  // Check if user can access admin features
  const canAccessAdminFeatures = () => {
    if (!user) return false;
    // For development, we'll simulate roles
    return isAdmin() || user.email?.endsWith('@admin.com') || true; // Temporary "true" for development
  };

  // Check if user can access mentor features
  const canAccessMentorFeatures = () => {
    if (!user) return false;
    // For development, we'll simulate roles
    return isMentor() || isAdmin() || 
      user.email?.endsWith('@mentor.com') || user.email?.endsWith('@admin.com') || true; // Temporary "true" for development
  };

  // Check if user can access student features
  const canAccessStudentFeatures = () => {
    if (!user) return false;
    // All authenticated users are considered students by default
    return true;
  };

  // Check if user can access agentic features
  const canAccessAgenticFeatures = () => {
    if (!user) return false;
    return true; // For now, all authenticated users can access agentic features
  };

  // Reset view mode to default
  const resetViewMode = () => {
    setViewMode('default');
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        isLoading,
        userRoles,
        viewMode,
        setViewMode,
        signIn,
        signInWithPassword,
        signUp,
        signOut,
        updateProfile,
        refreshProfile,
        isAdmin,
        isMentor,
        isStudent,
        canAccessAdminFeatures,
        canAccessMentorFeatures,
        canAccessStudentFeatures,
        canAccessAgenticFeatures,
        resetViewMode
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
